import { useState, useEffect, useCallback } from 'react';

import { InboxOutlined, MailOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Upload,
  Typography,
  Descriptions,
  Modal,
  message,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/lib/upload';

import { useMyCompanies } from '@/features/login/api/getMyCompanies';
import { createServiceTicket } from '@/features/services/layouts/ServiceTable/zendesk';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentCompanyId } from '@/hooks/useCurrentCompanyId';
import { useDisclosure } from '@/hooks/useDisclosure';

const { TextArea } = Input;
const { Dragger } = Upload;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const PRIORITY_OPTIONS = [
  { value: 'alto', label: 'Alta' },
  { value: 'medio', label: 'Média' },
  { value: 'baixo', label: 'Baixa' },
];

const beforeUpload = (file: RcFile) => {
  if (file.size > MAX_FILE_SIZE) {
    message.error(`O arquivo ${file.name} é muito grande. Tamanho máximo: 50MB`);
    return false;
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    message.warning(`Tipo de arquivo ${file.type} pode não ser suportado pelo Zendesk`);
  }

  return false;
};

type SupportRequestDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    description: string;
    date: string;
    local: string;
  };
};

type FormValues = {
  message: string;
  priority: 'alto' | 'medio' | 'baixo';
  files: UploadFile[];
  emails: string[];
};

export const SupportRequestDrawer = ({ isOpen, onClose, event }: SupportRequestDrawerProps) => {
  const [form] = Form.useForm<FormValues>();
  const { getAuthUser } = useAuth();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const companyId = useCurrentCompanyId();
  const { data: companiesResponse } = useMyCompanies({});
  const modal = useDisclosure();
  const [userEmail, setUserEmail] = useState<string>('');
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setFileList([]);
      const user = getAuthUser();
      if (user?.email) {
        setUserEmail(user.email);
        const initialEmails = [user.email];
        form.setFieldsValue({
          priority: 'medio',
          emails: initialEmails,
        });
      }
    }
  }, [isOpen, form, getAuthUser]);

  const handleEmailDeselect = useCallback(
    (value: string) => {
      if (value === userEmail) {
        setTimeout(() => {
          message.warning('Não é possível remover o seu email');
          const currentEmails = form.getFieldValue('emails') || [];
          form.setFieldValue('emails', Array.from(new Set([...currentEmails, value])));
        }, 0);
        return;
      }
      setAdditionalEmails((prev) => prev.filter((email) => email !== value));
    },
    [userEmail, form],
  );

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
    form.setFieldValue('files', newFileList);
  };

  const validateEmails = (_: unknown, value: string[]): Promise<void> => {
    if (!value?.length) {
      return Promise.reject(new Error('Pelo menos um email é necessário'));
    }

    if (!value.includes(userEmail)) {
      return Promise.reject(new Error('O seu email é obrigatório'));
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = value.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      return Promise.reject(new Error('Um ou mais emails são inválidos'));
    }

    return Promise.resolve();
  };

  const allEmails = [userEmail, ...additionalEmails].filter(Boolean);

  const handleEmailChange = (values: string[]) => {
    const requiredEmails = [userEmail];
    const missingRequired = requiredEmails.filter((email) => !values.includes(email));
    let updatedValues = values;

    if (missingRequired.length > 0) {
      updatedValues = [...values, ...missingRequired];
    }

    const additionals = updatedValues.filter((email) => email !== userEmail);
    setAdditionalEmails(additionals);
    form.setFieldValue('emails', updatedValues);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);
      const user = getAuthUser();

      if (!user) {
        throw new Error('Usuário não está autenticado');
      }

      const currentCompany = companiesResponse?.data?.data?.find(
        (company) => company.id === companyId,
      );

      if (!currentCompany) {
        throw new Error(
          'Empresa não encontrada. Por favor, verifique se você está logado com a empresa correta.',
        );
      }

      const organizationName = currentCompany.name;
      const requester = {
        name: user.displayName || 'Nome do Usuário',
        email: user.email || '',
      };

      const ticketMessage = [
        'Solicitação de Apoio - Evento do Calendário',
        '',
        'Mensagem do solicitante:',
        values.message,
        '',
        'Detalhes do Evento:',
        `- Título: ${event.title}`,
        `- Data: ${event.date}`,
        `- Local: ${event.local}`,
        `- Descrição: ${event.description}`,
        '',
        'Detalhes adicionais:',
        `- Empresa: ${organizationName}`,
        `- Prioridade: ${values.priority}`,
        `- Emails para contato: ${values.emails?.join(', ') || 'Não especificado'}`,
      ].join('\n');

      const files = fileList
        .map((file) => file.originFileObj)
        .filter((file): file is RcFile => file !== undefined);

      await createServiceTicket(
        organizationName,
        {
          item: `Evento: ${event.title}`,
          categoria: 'SUPORTE',
          detalhamento: 'Solicitação de apoio para evento do calendário',
        },
        ticketMessage,
        requester,
        {
          to: [],
          emails: values.emails || [],
          files,
        },
        undefined,
        values.priority,
      );

      message.success('Solicitação enviada com sucesso!');
      onClose();
      form.resetFields();
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('ERR_FAILED') &&
        error.message.includes('201')
      ) {
        message.success('Solicitação enviada com sucesso!');
        onClose();
        form.resetFields();
      } else {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Ocorreu um erro ao enviar sua solicitação. Por favor, tente novamente.';
        message.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        title={`Solicitar Apoio - ${event.title}`}
        placement="right"
        width={500}
        onClose={onClose}
        open={isOpen}
        extra={
          <Space style={{ marginLeft: '24px' }}>
            <Button data-testid="closeServiceDrawer" onClick={modal.open}>
              Cancelar
            </Button>
            <Button
              type="primary"
              data-testid="submitServiceDrawer"
              htmlType="submit"
              form="supportRequestForm"
              loading={isSubmitting}
            >
              Enviar Solicitação
            </Button>
          </Space>
        }
      >
        <Form
          id="supportRequestForm"
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          initialValues={{
            priority: 'medio',
            emails: [userEmail].filter(Boolean),
          }}
        >
          <Space direction="vertical" className="u-w-full">
            <Form.Item
              name="message"
              label="Mensagem"
              rules={[{ required: true, message: 'Por favor, insira uma mensagem' }]}
            >
              <TextArea rows={6} placeholder="Descreva sua solicitação..." />
            </Form.Item>

            <Form.Item
              name="priority"
              label="Prioridade"
              rules={[{ required: true, message: 'Por favor, selecione a prioridade' }]}
            >
              <Select placeholder="Selecione a prioridade" options={PRIORITY_OPTIONS} />
            </Form.Item>

            <Form.Item
              name="emails"
              label="Emails para contato"
              rules={[
                { required: true, message: 'Por favor, informe pelo menos um email' },
                { validator: validateEmails },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Adicione emails para contato"
                suffixIcon={<MailOutlined />}
                value={allEmails}
                onChange={handleEmailChange}
                onDeselect={handleEmailDeselect}
                maxTagCount="responsive"
                style={{ width: '100%' }}
              >
                <Select.Option value={userEmail} disabled>
                  {userEmail} (seu email)
                </Select.Option>
                {additionalEmails.map((email) => (
                  <Select.Option key={email} value={email}>
                    {email}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="files" label="Anexar arquivos (opcional)">
              <Dragger
                multiple
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                onRemove={(file) => {
                  const newFileList = fileList.filter((item) => item.uid !== file.uid);
                  setFileList(newFileList);
                  form.setFieldValue('files', newFileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Clique ou arraste arquivos para esta área</p>
                <p className="ant-upload-hint">
                  Upload de múltiplos arquivos (máximo 50MB por arquivo)
                </p>
              </Dragger>
            </Form.Item>

            <div style={{ marginTop: '24px' }}>
              <Typography.Title level={5}>Detalhes do Evento</Typography.Title>
              <Descriptions column={1}>
                <Descriptions.Item label="Título">{event.title}</Descriptions.Item>
                <Descriptions.Item label="Data">{event.date}</Descriptions.Item>
                <Descriptions.Item label="Local">{event.local}</Descriptions.Item>
                <Descriptions.Item label="Descrição">{event.description}</Descriptions.Item>
              </Descriptions>
            </div>
          </Space>
        </Form>
      </Drawer>

      <Modal
        title="Deseja realmente cancelar a solicitação?"
        open={modal.isOpen}
        okButtonProps={{ danger: true }}
        okText="Cancelar"
        cancelText="Voltar"
        onOk={() => {
          modal.close();
          onClose();
          form.resetFields();
        }}
        onCancel={modal.close}
      >
        <p>
          Todas as informações inseridas para solicitação serão perdidas e não poderão ser
          recuperadas futuramente.
        </p>
      </Modal>
    </>
  );
};
